import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, FileText, Lightbulb, Target, Star, TrendingUp, Mail, Download, ExternalLink, CheckSquare } from 'lucide-react';
import { StepCard, InfoCard, ExampleCard } from './SectionComponents';

const MarketingCaseStudySection = ({ onBack, onNext, nextSectionTitle, hasNext, onComplete, isCompleted }) => {

  const caseStudyTypes = [
    "Propose a campaign (paid, organic, brand or partnerships)",
    "Suggest messaging for a new product or audience",
    "Improve an existing landing page, funnel or comms flow"
  ];

  const principles = [
    {
      icon: FileText,
      title: "Show your logic",
      description: "Don't jump straight to a solution. Walk through your thinking – why this approach, what assumptions have you made and how you considered trade-offs.",
      number: 1
    },
    {
      icon: Target,
      title: "Ask: where's the value add?",
      description: "Once you've completed the task, go one step further. Can you suggest how to implement it? Draft a cold email? Add bonus ideas?",
      number: 2
    },
    {
      icon: TrendingUp,
      title: "Make more effort than suggested",
      description: "If they say it will take 4 hours, spend 8 on it – \"just enough\" is never going to be enough to make you stand out",
      number: 3
    }
  ];

  const goodPractices = [
    "Follow a clear structure (intro → analysis → recommendation → next steps)",
    "Have clear reasoning, even when making assumptions",
    "Stay scoped – don't try to boil the ocean",
    "Explain assumptions or unknowns and show prioritisation",
    "Include bonus elements where relevant (e.g. comms drafts, outreach mockups, process flows)"
  ];

  const caseStudyExamples = [
    {
      title: "Content campaign strategy – early-stage B2B SaaS",
      tags: ["Google Slides deck", "Loom walkthrough"],
      type: "success"
    },
    {
      title: "Landing page audit & messaging rework – D2C brand",
      tags: ["PDF Example", "Loom breakdown"],
      type: "tip"
    },
    {
      title: "Growth strategy for a pre-launch app",
      tags: ["Notion Case Study", "Feedback Loom"],
      type: "warning"
    }
  ];

  const bonusResources = [
    {
      text: "Really Good Emails",
      url: "https://reallygoodemails.com/",
      icon: Mail,
      className: "bg-orange-500 text-white hover:bg-orange-600"
    },
    {
      text: "PowerPoint Template",
      url: "https://drive.google.com/file/d/186djjpEeyUbsnrj-AtP-AI0fjCGgUrli/view",
      icon: Download,
      className: "bg-blue-500 text-white hover:bg-blue-600"
    }
  ];

  const exampleLearnings = [
    "Break ideas into simple chunks",
    "Explain logic behind choices",
    "Use visual aids to keep things clear"
  ];

  const checklist = [
    "Read the brief fully – highlight key goals or constraints",
    "Structured your response – intro, analysis, recommendation",
    "Stayed within scope and time limits – quality over length",
    "Stated assumptions – explain if something was unclear",
    "Finished with a summary and even better, \"what I'd explore next\""
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              The take-home case study
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Turn your case study into a showcase of your strategic thinking and execution skills.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          <StepCard title="📚 How to approach a take-home case study">
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-6 text-lg">Marketing case studies might ask you to:</h4>
                <div className="grid gap-4">
                  {caseStudyTypes.map((type, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-2 h-2 bg-[#02B491] rounded-full mt-2"></div>
                      <p className="text-slate-700 leading-relaxed">{type}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <InfoCard
                icon={Lightbulb}
                title="Key principle"
                description="You're not being tested for a perfect answer – you're being assessed on how you think, structure and communicate."
                type="tip"
              />
            </div>
          </StepCard>

          <StepCard title="⭐ Rules of thumb & what good looks like">
            <div className="space-y-10">
              <div>
                <h4 className="font-bold text-gray-900 mb-8 text-xl">Three principles to always keep in mind:</h4>
                <div className="space-y-8">
                  {principles.map((principle, index) => (
                    <div key={index} className="bg-white rounded-2xl p-8 border border-gray-200">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-[#02B491] rounded-xl flex items-center justify-center">
                          <span className="text-white font-bold text-lg">{principle.number}</span>
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900 mb-3">{principle.title}</h4>
                          <p className="text-slate-700 leading-relaxed">{principle.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-6 bg-emerald-50 rounded-2xl">
                  <p className="text-emerald-800 font-medium">
                    💡 These "extra 5%" touches often make the difference between a good and great submission.
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-6 text-xl">What does good look like?</h4>
                <div className="bg-gray-50 rounded-2xl p-8">
                  <h5 className="font-semibold text-gray-900 mb-6">The best case study submissions:</h5>
                  <div className="grid gap-4">
                    {goodPractices.map((practice, i) => (
                      <div key={i} className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-xl flex items-center justify-center mt-1">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                        <p className="text-slate-700 leading-relaxed">{practice}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="🏆 Best-in-class examples & templates">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                Explore previous case studies to see how successful candidates structured their thinking:
              </p>
              
              <div className="grid gap-6 md:grid-cols-1">
                {caseStudyExamples.map((example, index) => (
                  <div key={index} className="bg-white rounded-2xl p-8 border border-gray-200">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-3">{example.title}</h4>
                        <div className="flex flex-wrap gap-2">
                          {example.tags.map((tag, tagIndex) => (
                            <span key={tagIndex} className="inline-flex items-center px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
                <h4 className="font-bold text-amber-900 mb-6 text-lg flex items-center">
                  <Lightbulb className="w-6 h-6 mr-3" />
                  Pay attention to how these examples:
                </h4>
                <div className="grid gap-4">
                  {exampleLearnings.map((learning, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                      <p className="text-amber-800 leading-relaxed">{learning}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="🛠️ Bonus tools & inspiration">
            <div className="space-y-8">
              <div className="grid gap-8 md:grid-cols-2">
                <div className="bg-white rounded-2xl p-8 border border-gray-200">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Excellent email inspiration</h3>
                      <p className="text-slate-700 leading-relaxed">Explore great comms & email design</p>
                    </div>
                  </div>
                  <a
                    href="https://reallygoodemails.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Visit Site
                    <ExternalLink className="w-3 h-3 ml-2" />
                  </a>
                </div>

                <div className="bg-white rounded-2xl p-8 border border-gray-200">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                      <Download className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">PowerPoint slide template</h3>
                      <p className="text-slate-700 leading-relaxed">Use this if you want to structure your case in slide form</p>
                    </div>
                  </div>
                  <a
                    href="https://drive.google.com/file/d/186djjpEeyUbsnrj-AtP-AI0fjCGgUrli/view"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                    <ExternalLink className="w-3 h-3 ml-2" />
                  </a>
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="✅ Case study submission checklist">
            <div className="space-y-8">
              <p className="text-slate-600 text-lg leading-relaxed">
                Before sending your draft, make sure you've:
              </p>
              
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="space-y-4">
                  {checklist.map((item, index) => (
                    <label key={index} className="flex items-start space-x-4 cursor-pointer group">
                      <input 
                        type="checkbox" 
                        className="mt-1.5 rounded border-gray-300 text-[#02B491] focus:ring-[#02B491] focus:ring-offset-0" 
                      />
                      <span className="text-slate-700 leading-relaxed group-hover:text-slate-900 transition-colors">
                        {item}
                      </span>
                    </label>
                  ))}
                </div>
                
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <p className="text-slate-500 text-sm italic">
                    Optional: use slides or visuals if it helps you communicate better.
                  </p>
                </div>
              </div>
            </div>
          </StepCard>

        </div>
      </div>

        
        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default MarketingCaseStudySection; 